"use client";

import { useState, useEffect } from "react";
import {
    Button,
    Table,
    Tag,
    Drawer,
    Form,
    Input,
    Spin,
    message,
    Switch,
    Space,
    Popconfirm,
    InputNumber,
    Select,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import useActivityGroupsStore, { ActivityGroup, Category } from "./ActivityGroupsStore.ts";
import useCategoriesStore from "../Categories/CategoriesStore.ts";

export default function ActivityGroups() {
    const {
        getGroups,
        getGroupById,
        createGroup,
        updateGroup,
        deleteGroup,
        syncCategories,
        groupsData,
        groupDetail,
        isListLoading,
        isDetailLoading,
        isSaving,
    } = useActivityGroupsStore();

    const {
        getCategories,
        categoriesData,
        isListLoading: isCategoriesLoading,
    } = useCategoriesStore();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchGroups();
        fetchCategories();
    }, [currentPage, pageSize]);

    const fetchCategories = () => {
        // Fetch all categories without pagination
        getCategories({ per_page: 1000 });
    };

    const fetchGroups = () => {
        const params = {
            page: currentPage,
            per_page: pageSize,
        };
        getGroups(params);
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
    };

    const handleAddGroup = () => {
        setIsEditMode(false);
        form.resetFields();
        setIsDrawerOpen(true);
    };

    const handleEditGroup = async (id: number) => {
        setIsEditMode(true);
        setIsDrawerOpen(true);
        form.resetFields();
        const result = await getGroupById(id);
        if (result.success && result.data) {
            // Transform categories from API response to form format
            const formData = {
                ...result.data,
                categories: result.data.categories?.map(cat => ({
                    category_id: cat.id,  // API returns 'id', form needs 'category_id'
                    display_order: cat.display_order,
                })) || [],
            };

            console.log('📝 Loading activity group detail:', formData);
            form.setFieldsValue(formData);
        } else {
            message.error("Failed to load group details");
        }
    };

    const handleDeleteGroup = async (id: number) => {
        const result = await deleteGroup(id);
        if (result.success) {
            fetchGroups();
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Format categories if present
            if (values.categories) {
                // Ensure categories is an array of objects
            }

            let result;
            if (isEditMode && groupDetail?.id) {
                result = await updateGroup(groupDetail.id, values);
            } else {
                result = await createGroup(values);
            }

            if (result.success) {
                setIsDrawerOpen(false);
                fetchGroups();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const columns: ColumnsType<ActivityGroup> = [
        {
            title: <span className="font-semibold">Key</span>,
            dataIndex: "key",
            key: "key",
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: <span className="font-semibold">Name</span>,
            dataIndex: "name",
            key: "name",
            render: (text) => <span className="font-medium text-gray-900">{text}</span>,
        },
        {
            title: <span className="font-semibold">Description</span>,
            dataIndex: "description",
            key: "description",
            ellipsis: true,
        },
        {
            title: <span className="font-semibold">Order</span>,
            dataIndex: "display_order",
            key: "display_order",
            align: "center",
        },
        {
            title: <span className="font-semibold">Status</span>,
            dataIndex: "is_active",
            key: "is_active",
            render: (isActive) => (
                <Tag color={isActive ? "success" : "error"}>
                    {isActive ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: <span className="font-semibold">Actions</span>,
            key: "action",
            align: "center",
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<i className="fa-solid fa-pen-to-square"></i>}
                        type="text"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEditGroup(record.id)}
                    />
                    <Popconfirm
                        title="Delete this group?"
                        description="Are you sure to delete this activity group?"
                        onConfirm={() => handleDeleteGroup(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<i className="fa-solid fa-trash"></i>}
                            type="text"
                            danger
                            className="hover:bg-red-50"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const paginationConfig = {
        current: groupsData?.current_page || 1,
        pageSize: groupsData?.per_page || 10,
        total: groupsData?.total || 0,
        showSizeChanger: true,
        showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} groups`,
    };

    return (
        <>
            <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
                    <span>Home</span>
                    <span>/</span>
                    <span className="text-black">Activity Groups</span>
                </div>
                <h1 className="text-3xl font-bold text-black">Activity Groups</h1>
            </div>

            <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
                <div className="p-6">
                    <div className="mb-6 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-primary-600">
                                <i className="fa-solid fa-layer-group text-black text-lg"></i>
                            </div>
                            <h3 className="text-lg font-bold text-black m-0">All Groups</h3>
                        </div>
                        <Button
                            type="primary"
                            icon={<i className="fa-solid fa-plus text-black"></i>}
                            className="shadow-lg shadow-yellow-200/50"
                            onClick={handleAddGroup}
                        >
                            Add New Group
                        </Button>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={groupsData?.data}
                        loading={isListLoading}
                        rowKey="id"
                        pagination={paginationConfig}
                        onChange={handleTableChange}
                    />
                </div>
            </div>

            <Drawer
                title={
                    <span className="flex items-center gap-2 text-lg">
                        <i className="fa-solid fa-layer-group text-black"></i>
                        {isEditMode ? "Edit Activity Group" : "New Activity Group"}
                    </span>
                }
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                width={600}
                destroyOnClose
                footer={
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setIsDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={handleSave} loading={isSaving}>
                            Save
                        </Button>
                    </div>
                }
            >
                {isDetailLoading ? (
                    <div className="flex justify-center items-center h-40">
                        <Spin tip="Loading..." />
                    </div>
                ) : (
                    <Form form={form} layout="vertical" className="pt-4">
                        <Form.Item
                            name="key"
                            label="Key"
                            rules={[{ required: true, message: "Please enter key" }]}
                        >
                            <Input placeholder="e.g., food" />
                        </Form.Item>

                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[{ required: true, message: "Please enter name" }]}
                        >
                            <Input placeholder="e.g., Ăn uống" />
                        </Form.Item>

                        <Form.Item
                            name="description"
                            label="Description"
                        >
                            <Input.TextArea rows={3} placeholder="Description..." />
                        </Form.Item>

                        <div className="grid grid-cols-2 gap-4">
                            <Form.Item
                                name="display_order"
                                label="Display Order"
                                initialValue={1}
                            >
                                <InputNumber min={0} className="w-full" />
                            </Form.Item>

                            <Form.Item
                                name="is_active"
                                label="Status"
                                valuePropName="checked"
                                initialValue={true}
                            >
                                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                            </Form.Item>
                        </div>

                        {/* Categories Section */}
                        <div className="mt-6">
                            <h4 className="text-sm font-semibold mb-3">Categories Mapping</h4>
                            <Form.List name="categories">
                                {(fields, { add, remove }) => (
                                    <div className="space-y-3">
                                        {fields.map(({ key, name, ...restField }) => (
                                            <div key={key} className="flex gap-2 items-start bg-gray-50 p-3 rounded-xl">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'category_id']}
                                                    rules={[{ required: true, message: 'Missing category' }]}
                                                    className="mb-0 flex-1"
                                                >
                                                    <Select
                                                        placeholder="Select Category"
                                                        showSearch
                                                        optionFilterProp="children"
                                                        loading={isCategoriesLoading}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={categoriesData?.data.map((cat) => ({
                                                            value: cat.id,
                                                            label: `${cat.display_name} (${cat.name})`,
                                                        })) || []}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'display_order']}
                                                    initialValue={0}
                                                    className="mb-0 w-20"
                                                >
                                                    <InputNumber placeholder="Order" className="w-full" />
                                                </Form.Item>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<i className="fa-solid fa-trash"></i>}
                                                    onClick={() => remove(name)}
                                                />
                                            </div>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<i className="fa-solid fa-plus"></i>}>
                                            Add Category Mapping
                                        </Button>
                                    </div>
                                )}
                            </Form.List>
                        </div>
                    </Form>
                )}
            </Drawer>
        </>
    );
}
